/**
 * GuestWelcome — dashboard shown while exploring without a session.
 * Warm invitation to browse the public catalogues; account CTAs without
 * pressure (anti-culpa: invite, never scold).
 */
import React from 'react';
import { Box, Card, CardActionArea, CardContent, Typography, Button, Grid } from '@mui/material';
import {
  Restaurant as RestaurantIcon,
  MenuBook as MenuBookIcon,
  SwapHoriz as SwapHorizIcon,
  Spa as SpaIcon,
  Calculate as CalculateIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const EXPLORE = [
  { id: 'foods', icon: RestaurantIcon, title: 'Alimentos', blurb: 'Base de datos nutricional mexicana', color: '#1F5C3D' },
  { id: 'recipes', icon: MenuBookIcon, title: 'Recetas', blurb: 'Cocina inteligente, paso a paso', color: '#E0892E' },
  { id: 'equivalences', icon: SwapHorizIcon, title: 'Equivalencias', blurb: 'Sistema SMAE interactivo', color: '#2C7D8C' },
  { id: 'medicinal-plants', icon: SpaIcon, title: 'Plantas medicinales', blurb: 'Atlas UNAM con evidencia', color: '#2E8B57' },
  { id: 'calculator', icon: CalculateIcon, title: 'Calculadora', blurb: 'BMR, TDEE y macros al momento', color: '#B8531F' },
];

const GuestWelcome = ({ onNavigate }) => {
  const navigate = useNavigate();

  return (
    <Box sx={{ width: '100%', maxWidth: 960, mx: 'auto' }}>
      <Box sx={{ mb: { xs: 2.5, sm: 4 } }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
        >
          Bienvenida, bienvenido 🌿
        </Typography>
        {/* El texto largo solo en pantalla grande: en el teléfono ocupaba
            media pantalla antes de dejar ver una sola sección. */}
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ maxWidth: 620, lineHeight: 1.6, display: { xs: 'none', sm: 'block' } }}
        >
          Esta es la app real de Nutrition Intelligence — explórala libremente.
          Cuando quieras guardar tu diario, tu plan o tu progreso, crea tu
          cuenta gratis y todo esto se vuelve tuyo.
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.5, display: { xs: 'block', sm: 'none' } }}
        >
          La app real, para explorar. Crea tu cuenta cuando quieras guardar.
        </Typography>
      </Box>

      {/* En teléfono son filas compactas (las cinco caben de un vistazo);
          en escritorio, la retícula de tarjetas de siempre. */}
      <Grid container spacing={{ xs: 1, sm: 2.5 }} sx={{ mb: 4 }}>
        {EXPLORE.map(({ id, icon: Icon, title, blurb, color }) => (
          <Grid item xs={12} sm={6} md={4} key={id}>
            <Card elevation={1} sx={{ height: '100%' }}>
              <CardActionArea
                onClick={() => onNavigate && onNavigate(id)}
                sx={{ height: '100%', minHeight: { xs: 64, sm: 120 } }}
                aria-label={`Explorar ${title}`}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'row', sm: 'column' },
                    alignItems: { xs: 'center', sm: 'flex-start' },
                    gap: { xs: 1.75, sm: 0 },
                    py: { xs: 1.5, sm: 2 },
                    '&:last-child': { pb: { xs: 1.5, sm: 2 } },
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      flex: '0 0 auto',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: `${color}1F`,
                      mb: { xs: 0, sm: 1.5 },
                    }}
                  >
                    <Icon sx={{ color }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={600} noWrap>{title}</Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: 13, sm: 14 } }}
                    >
                      {blurb}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card elevation={0} sx={{ bgcolor: '#E2EBDD', border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>¿Te gustó lo que ves?</Typography>
            <Typography variant="body2" color="text.secondary">
              Con tu cuenta gratis guardas tu diario, tus planes y tu avance. Sin tarjeta, sin publicidad.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button variant="text" onClick={() => navigate('/auth/login')} sx={{ fontWeight: 600, minHeight: 44 }}>
              Iniciar sesión
            </Button>
            <Button variant="contained" color="secondary" onClick={() => navigate('/auth/register')} sx={{ fontWeight: 700, minHeight: 44 }}>
              Crear cuenta gratis
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GuestWelcome;
